import React, { useEffect, useMemo } from "react";
import { ClipboardList, TriangleAlert } from "lucide-react";
import { useRoundPlanningStore } from "../../store/store";
import { useRoundIssuesStore } from "../../store/RoundIssuesStore";

const SafetyFacilityRoundsDashboard: React.FC = () => {
  const { allRoundPlanning, loading, getAllRoundPlanning } =
    useRoundPlanningStore();
  const { issues, error, dashboardStats, fetchIssues, fetchDashboardStats } =
    useRoundIssuesStore();
  useEffect(() => {
    fetchIssues();
    const interval = setInterval(() => {
      fetchDashboardStats();
    }, 30000); // Poll every 30s for stats
    fetchDashboardStats();
    return () => clearInterval(interval);
  }, [fetchIssues, fetchDashboardStats]);
  useEffect(() => {
    getAllRoundPlanning();
  }, [getAllRoundPlanning]);

  const { totalRounds, completedRounds, completionRate } = useMemo(() => {
    const total = allRoundPlanning?.length || 0;
    const completed =
      allRoundPlanning?.filter(
        (round: any) =>
          String(round?.status || "").toLowerCase() === "completed",
      ).length || 0;

    const rate = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      totalRounds: total,
      completedRounds: completed,
      completionRate: rate,
    };
  }, [allRoundPlanning]);
  const { criticalIssues, totalIssues } = useMemo(() => {
    const critical = dashboardStats?.bySeverity?.critical || 0;
    const total = Object.values(dashboardStats?.bySeverity || {}).reduce(
      (sum: number, count: any) => sum + (count || 0),
      0,
    );
    return { criticalIssues: critical, totalIssues: total };
  }, [dashboardStats]);
  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 mb-2">
          Safety & Facility Rounds Dashboard
        </h1>
        <p className="text-gray-600">
          Manage checklists, inspections, and facility rounds
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200 border border-gray-200 hover:border-[#00A8A5]">
          <div className="flex items-start gap-4">
            <div
              className={` p-3 rounded-lg text-white bg-blue-500 flex-shrink-0`}
            >
              <ClipboardList className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Rounds Completion Rate
              </h3>
              <div className="text-sm text-gray-600 mb-4">
                {loading ? (
                  <span>Loading...</span>
                ) : (
                  <div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {completionRate}%
                    </div>
                    <div className="text-md text-gray-700">
                      ({completedRounds} completed / {totalRounds} total)
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200 border border-gray-200 hover:border-[#00A8A5]">
          <div className="flex items-start gap-4">
            <div
              className={` p-3 rounded-lg text-white bg-red-500 flex-shrink-0`}
            >
              <TriangleAlert className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Critical Issues
              </h3>
              <div className="text-sm text-gray-600 mb-4">
                {loading ? (
                  <span>Loading...</span>
                ) : (
                  <div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {dashboardStats?.bySeverity.critical || 0}{" "}
                    </div>
                    <div className="text-md text-gray-700">
                      {totalIssues > 0
                        ? `(${criticalIssues} critical / ${totalIssues} total)`
                        : "(No issues reported)"}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyFacilityRoundsDashboard;
